<template>
  <section v-if="isGetRequestPending" class="edit-organization">
    <p-loading />
  </section>
  <section v-else-if="!item" class="edit-organization">
    <p-not-found />
  </section>
  <section v-else class="edit-organization">
    <header>
      <h1>Edit Organization</h1>
      <div>
        <div class="action-wrapper">
          <p-button v-if="$hasPermission('organizations.write')" color="secondary" @click="confirmDelete">Delete</p-button>
        </div>
      </div>
    </header>
    <form :class="{ dirty: $v.$anyDirty }" autocomplete="off" @submit.prevent="submit">
      <div class="form-row">
        <p-text-field
          v-model="name"
          :label="'Name'"
          autocomplete="off"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
        />
        <div v-if="!$v.name.required" class="error">Name is required</div>
      </div>
      <div class="form-row">
        <p-tags
          v-model="aliases"
          :label="'Aliases'"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          placeholder=""
          :options="[]"
        />
      </div>
      <div class="form-row">
        <p-tags
          v-model="customerNumbers"
          :label="'Customer Numbers'"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          placeholder=""
          :options="[]"
        />
      </div>
      <div class="form-row">
        <p-text-field v-model="shortName" :label="'Prefix'" autocomplete="off" :disabled="false" />
        <div v-if="!$v.shortName.required" class="error">Short name is required</div>
      </div>
      <div class="form-row">
        <p-multiselect
          v-model="type"
          :options="organizationTypeOptions"
          :get-label-callback="v => v"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          label="Organization Type"
          placeholder="Select type"
          :searchable="false"
          :multiple="false"
        />
      </div>
      <div class="form-row logo-section">
        <label>Organization Logo</label>
        <div v-if="logoPreview || currentLogoUrl" class="logo-preview">
          <img :src="logoPreview || currentLogoUrl" alt="Organization logo" />
          <p-button v-if="$hasPermission('organizations.write')" color="secondary" type="button" @click="removeLogo">Remove Logo</p-button>
        </div>
        <p-file-select
          v-else
          accept="image/*"
          label=""
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          @change="handleLogoChange"
        >
          Upload Logo
        </p-file-select>
      </div>
      <div class="form-row">
        <p-multiselect
          v-model="models"
          :options="getModelOptions()"
          :get-label-callback="getModelName"
          :disabled="!$hasPermission('administration')"
          label="Models (specify models that this organization can use)"
          placeholder="Select models"
          :searchable="true"
          :multiple="true"
        ></p-multiselect>
      </div>
      <div class="form-row">
        <p-tags
          v-model="domains"
          :label="'Correspondence domains'"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          placeholder=""
          :options="[]"
        />
      </div>
      <div class="form-row">
        <p-tags
          v-model="regexs"
          :label="'Regexs'"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          placeholder=""
          :options="[]"
          @input="validateMappings"
        />
      </div>
      <div class="form-row">
        <p-checkbox
          v-model="enablePrivateOcr"
          label="Enable vendor OCR"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
        />
      </div>
      <div class="form-row">
        <p-checkbox
          v-model="enableSpeechToText"
          label="Enable speech to text"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
        />
      </div>
      <div v-if="enableSpeechToText" class="form-row">
        <p-multiselect
          v-model="selectedSpeechService"
          :options="speechToTextOptions"
          :get-label-callback="getSpeechServiceLabel"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          label="Speech to text service/model"
          placeholder="Select speech to text service or model"
          :searchable="false"
          :multiple="false"
        />
      </div>
      <div v-if="!$v.selectedSpeechService.required && $v.selectedSpeechService.$dirty && enableSpeechToText" class="error">
        Speech to text service/model is required
      </div>
      <div v-if="enableSpeechToText && isSpeechModelSelected" class="form-row">
        <p-multiselect
          v-model="selectedSpeechPostProcessingModel"
          :options="chatModelsOptions"
          :get-label-callback="getChatModelLabel"
          :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          label="Speech post processing model"
          placeholder="Select chat model"
          :searchable="false"
          :multiple="false"
        />
      </div>
      <div v-if="namedGroupsRegexps && namedGroupsRegexps.length" class="form-row">
        <RegexpMappings v-model="mappings" :regexps="namedGroupsRegexps" />
      </div>
      <div class="form-row binding-section">
        <label>Binding Constructors</label>
        <div v-for="(bc, i) in bindingConstructors" :key="i" class="bc-row">
          <p-text-field v-model="bc.name" :label="'Name'" :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')" />
          <p-text-field
            v-model="bc.valueTemplate"
            :label="'Value Template'"
            :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')"
          />
          <p-button v-if="$hasPermission('organizations.write')" type="button" @click="removeBindingConstructor(i)">
            <MdIcon size="sm" name="delete" />
          </p-button>
        </div>
        <p-button v-if="$hasPermission('organizations.write')" type="button" color="primary" variant="text" @click="addBindingConstructor"
          >Add Constructor</p-button
        >
      </div>
      <div v-if="isUpdateRequestFailed" class="error">{{ errorMessage }}</div>
      <div class="form-row submit">
        <p-button type="button" @click.prevent="$router.back()">Cancel</p-button>
        <p-button
          v-if="$hasPermission('organizations.write')"
          color="primary"
          type="submit"
          :disabled="isUpdateRequestPending || ($v.$anyDirty && $v.$invalid)"
          >Save</p-button
        >
      </div>
    </form>

    <footer></footer>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';

import Loading from '@/components/common/Loading';
import TextField from '@/components/common/TextField';
import Tags from '@/components/common/Tags';
import Button from '@/components/common/Button';
import NotFound from '@/components/NotFound';
import RegexpMappings from './RegexpMappings';
import Multiselect from '@/components/common/Multiselect';
import CheckBox from '@/components/common/Checkbox.vue';
import FileSelect from '@/components/common/FileSelect.vue';
import MdIcon from '@/components/common/MdIcon';

export default {
  components: {
    'p-text-field': TextField,
    'p-tags': Tags,
    'p-button': Button,
    'p-loading': Loading,
    'p-not-found': NotFound,
    'p-multiselect': Multiselect,
    'p-checkbox': CheckBox,
    'p-file-select': FileSelect,
    RegexpMappings,
    MdIcon
  },
  data() {
    return {
      name: '',
      shortName: '',
      aliases: [],
      customerNumbers: [],
      domains: [],
      regexs: [],
      errorMessage: '',
      mappings: {},
      models: [],
      type: null,
      enableSpeechToText: false,
      enablePrivateOcr: false,
      selectedSpeechService: null,
      selectedSpeechPostProcessingModel: null,
      logo: null,
      currentLogoUrl: '',
      logoPreview: null,
      bindingConstructors: []
    };
  },
  computed: {
    ...mapState({
      item: s => s.organizations.item,
      modelsCollection: s => s.nlp.models,
      isGetRequestPending: s => s.organizations.isGetRequestPending,
      isUpdateRequestPending: s => s.organizations.isUpdateRequestPending,
      isUpdateRequestFailed: s => s.organizations.isUpdateRequestFailed,
      isDeleteRequestPending: s => s.organizations.isDeleteRequestPending,
      isDeleteRequestFailed: s => s.organizations.isDeleteRequestFailed,
      sttServices: s => s.nlp.sttServices ?? []
    }),
    speechToTextOptions() {
      const service = this.sttServices?.map(s => ({ id: s.id, label: s.name, type: 'service' })) ?? [];

      const ids = Array.isArray(this.models) ? this.models.filter(x => typeof x === 'string') : [];

      const selectedModels = ids.map(id => this.modelsCollection.find(m => m.id === id)).filter(Boolean);

      const speechModels = selectedModels
        .filter(m => {
          if (Array.isArray(m.capabilities)) {
            return m.capabilities.some(cap => typeof cap === 'string' && cap.includes('speech-to-text'));
          }
          if (typeof m.capability === 'string') {
            return m.capability.includes('speech-to-text');
          }
          return false;
        })
        .map(m => ({
          id: m.id,
          label: m.name,
          type: 'model'
        }));

      return [...service, ...speechModels];
    },
    chatModelsOptions() {
      const ids = Array.isArray(this.models) ? this.models.filter(x => typeof x === 'string') : [];

      const selectedModels = ids.map(id => this.modelsCollection.find(m => m.id === id)).filter(Boolean);

      return selectedModels
        .filter(m => {
          if (Array.isArray(m.capabilities)) {
            return m.capabilities.some(cap => typeof cap === 'string' && cap.includes('chat'));
          }
          if (typeof m.capability === 'string') {
            return m.capability.includes('chat');
          }
          return false;
        })
        .map(m => ({ id: m.id, label: m.name }));
    },
    isSpeechModelSelected() {
      if (!this.selectedSpeechService) return false;
      const opt =
        typeof this.selectedSpeechService === 'object'
          ? this.selectedSpeechService
          : this.speechToTextOptions.find(x => x.id === this.selectedSpeechService);
      return opt && opt.type === 'model';
    },
    organizationTypeOptions() {
      return ['Client', 'Firm', 'Other'];
    },
    namedGroupsRegexps() {
      return this.regexs.filter(r =>
        r
          .toString()
          .match(/<([^>]+)>/g)
          ?.map(name => name.slice(1, -1))
      );
    }
  },
  validations: {
    name: { required },
    shortName: { required },
    selectedSpeechService: {
      required: requiredIf(function() {
        return this.enableSpeechToText;
      })
    }
  },
  watch: {
    '$route.params': {
      async handler() {
        await Promise.all([
          this.$store.dispatch('nlp/getSttServices'),
          this.$store.dispatch('nlp/getConfigurations'),
          this.$store.dispatch('organizations/getById', this.$route.params.id)
        ]);
        this.name = this.item && this.item.name;
        this.shortName = this.item && this.item.shortName;
        this.aliases = this.item && this.item.aliases;
        this.customerNumbers = this.item && this.item.customerNumbers;
        this.domains = this.item && this.item.domains;
        this.regexs = this.item && this.item.regexs;
        this.mappings = this.item && this.item.mappings;
        this.models = [...((this.item && this.item.models) ?? [])];
        this.enableSpeechToText = !!(this.item && this.item.enableSpeechToText);
        this.selectedSpeechService = this.getSpeechServiceById(
          this.item && this.item.speechToTextService && this.item.speechToTextService.id,
          this.item && this.item.speechToTextService && this.item.speechToTextService.type
        );
        this.selectedSpeechPostProcessingModel = this.getChatModelById(
          this.item && this.item.speechPostProcessingModel && this.item.speechPostProcessingModel.id
        );
        this.enablePrivateOcr = !!(this.item && this.item.enablePrivateOcr);
        this.currentLogoUrl = this.item && this.item.logoUrl ? this.item.logoUrl : '';
        this.logoPreview = null;
        this.logo = null;
        this.bindingConstructors = this.item && this.item.bindingConstructors ? JSON.parse(JSON.stringify(this.item.bindingConstructors)) : [];
        this.type = (this.item && this.item.type) || null;
      },
      immediate: true
    },
    speechToTextOptions(newOptions) {
      if (
        this.selectedSpeechService &&
        !newOptions.some(opt =>
          typeof this.selectedSpeechService === 'object' ? opt.id === this.selectedSpeechService.id : opt.id === this.selectedSpeechService
        )
      ) {
        this.selectedSpeechService = null;
      }
    },
    chatModelsOptions(newOptions) {
      if (
        this.selectedSpeechPostProcessingModel &&
        !newOptions.some(opt =>
          typeof this.selectedSpeechPostProcessingModel === 'object'
            ? opt.id === this.selectedSpeechPostProcessingModel.id
            : opt.id === this.selectedSpeechPostProcessingModel
        )
      ) {
        this.selectedSpeechPostProcessingModel = null;
      }
    }
  },
  methods: {
    getSpeechServiceLabel(option) {
      if (typeof option === 'string') {
        const found = this.speechToTextOptions.find(opt => opt.id === option);
        return found ? found.label : option;
      }
      return option.label || '';
    },
    getChatModelLabel(option) {
      if (typeof option === 'string') {
        const found = this.chatModelsOptions.find(opt => opt.id === option);
        return found ? found.label : option;
      }
      return option.label || '';
    },
    getModelOptions() {
      return this.modelsCollection?.map(m => m.id) ?? [];
    },
    getModelName(id) {
      return this.modelsCollection.find(model => model.id === id)?.name ?? 'Unknown Model';
    },
    getSpeechServiceById(id, type) {
      if (!id) return null;
      return this.speechToTextOptions.find(opt => opt.id === id && opt.type === type) || null;
    },
    getChatModelById(id) {
      if (!id) return null;
      return this.chatModelsOptions.find(opt => opt.id === id) || null;
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        this.errorMessage = '';

        let speechToTextService = null;
        if (this.enableSpeechToText && this.selectedSpeechService) {
          if (typeof this.selectedSpeechService === 'object') {
            speechToTextService = {
              id: this.selectedSpeechService.id,
              type: this.selectedSpeechService.type
            };
          } else {
            const found = this.speechToTextOptions.find(opt => opt.id === this.selectedSpeechService);
            speechToTextService = found ? { id: found.id, type: found.type } : { id: this.selectedSpeechService, type: null };
          }
        }

        let speechPostProcessingModel = null;
        if (this.enableSpeechToText && this.isSpeechModelSelected && this.selectedSpeechPostProcessingModel) {
          if (typeof this.selectedSpeechPostProcessingModel === 'object') {
            speechPostProcessingModel = {
              id: this.selectedSpeechPostProcessingModel.id,
              type: this.selectedSpeechPostProcessingModel.type || 'model'
            };
          } else {
            const found = this.chatModelsOptions.find(opt => opt.id === this.selectedSpeechPostProcessingModel);
            speechPostProcessingModel = found
              ? { id: found.id, type: found.type || 'model' }
              : { id: this.selectedSpeechPostProcessingModel, type: 'model' };
          }
        }

        const payload = {
          id: this.$route.params.id,
          name: this.name.trim(),
          shortName: this.shortName.trim(),
          aliases: Array.isArray(this.aliases) ? this.aliases : [],
          customerNumbers: Array.isArray(this.customerNumbers) ? this.customerNumbers : [],
          domains: Array.isArray(this.domains) ? this.domains : [],
          regexs: Array.isArray(this.regexs) ? this.regexs : [],
          mappings: Array.isArray(this.mappings) ? this.mappings : [],
          models: Array.isArray(this.models) ? this.models : [this.models],
          enableSpeechToText: !!this.enableSpeechToText,
          enablePrivateOcr: !!this.enablePrivateOcr,
          speechToTextService,
          speechPostProcessingModel,
          type: this.type || undefined,
          bindingConstructors: (this.bindingConstructors || []).filter(x => x && x.name)
        };

        if (this.logo) {
          payload.logo = this.logo;
        } else if (!this.currentLogoUrl && !this.logoPreview) {
          payload.logo = null;
        }

        await this.$store.dispatch('organizations/update', payload);

        if (!this.isUpdateRequestFailed) {
          this.$router.push({ path: `/organizations` });
        }
      } catch (e) {
        this.errorMessage =
          e?.response?.status === 409 ? 'Organization name already exists' : 'Failed to update organization. Check your input and try again.';
      }
    },
    back() {
      this.$router.back();
    },
    handleLogoChange(files) {
      if (files && files.length > 0) {
        const file = files[0];
        if (!file.type.startsWith('image/')) {
          this.errorMessage = 'Only image files are allowed for logo';
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          this.errorMessage = 'Logo file size must be less than 5MB';
          return;
        }
        const reader = new FileReader();
        reader.onload = e => {
          this.logo = e.target.result;
          this.logoPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    removeLogo() {
      this.logo = null;
      this.logoPreview = null;
      this.currentLogoUrl = '';
    },
    validateMappings() {
      this.mappings = this.mappings.filter(m => this.regexs.includes(m.regex));
    },
    async confirmDelete() {
      const { name } = this.item;

      const confirmResult = await this.$confirm({
        title: 'Delete organization?',
        message: `Are you sure you want to delete organization '${name}'? This action can't be undone.`,
        confirm: 'Delete'
      });

      if (!confirmResult) {
        return;
      }

      const lock = this.$lock();

      await this.$store.dispatch('organizations/delete', this.$route.params.id);

      lock.release();

      if (this.isDeleteRequestFailed) {
        this.$toast.error({
          title: 'Failed to delete organization',
          message: `Please, try again later or contact our development team.`
        });
      } else {
        this.$toast.success({
          title: 'Organization deleted',
          message: `Organization '${name}'  was successfully deleted.`
        });
        this.$router.push({ path: `/organizations` });
      }
    },
    addBindingConstructor() {
      this.bindingConstructors.push({ name: '', valueTemplate: '' });
    },
    removeBindingConstructor(i) {
      this.bindingConstructors.splice(i, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-organization {
  width: 100%;
  header {
    width: 100%;
    padding: 1rem 2rem;
    border-bottom: 1px solid var(--theme-highlight);
    box-sizing: border-box;
    position: relative;

    .action-wrapper {
      position: absolute;
      right: 2rem;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  h1 {
    color: var(--theme-on-surface);
    font-size: 1.5rem;
    font-weight: 700;
  }
  form {
    padding: 2rem;
    max-width: 480px;

    > * {
      &:not(:last-child) {
        margin-bottom: 1.5rem;
      }
      &:last-child {
        display: flex;
        justify-content: flex-end;
      }
    }

    .error {
      font-size: 0.8rem;
      color: var(--theme-error);
      text-align: left;
      padding: 0.25rem 0;
      display: none;
    }
    &.dirty {
      .error {
        display: block;
      }
    }
  }

  .binding-section {
    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--theme-on-surface);
    }
  }

  .bc-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 0.75rem;
    align-items: end;
    margin-bottom: 0.75rem;

    ::v-deep(.text-field) {
      width: 100%;
    }

    ::v-deep(button) {
      white-space: nowrap;
      height: 38px;
    }
  }

  .logo-section {
    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--theme-on-surface);
    }

    .logo-preview {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;

      img {
        max-width: 200px;
        max-height: 200px;
        border: 1px solid var(--theme-highlight);
        border-radius: 4px;
        object-fit: contain;
      }
    }
  }
}
</style>
